import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { GoogleLogo, Lightning } from "@phosphor-icons/react";
import { useAuth, formatApiErrorDetail } from "../context/AuthContext";
import { Disclaimer } from "../components/Header";

export default function AuthPage() {
  const [mode, setMode] = useState("login");
  const [form, setForm] = useState({ name: "", email: "", password: "" });
  const [ageConfirmed, setAgeConfirmed] = useState(false);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const { setUser, API } = useAuth();
  const navigate = useNavigate();

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (mode === "register" && !ageConfirmed) {
      setError("You must confirm you are 21 or older.");
      return;
    }
    setBusy(true);
    try {
      const url = mode === "login" ? `${API}/auth/login` : `${API}/auth/register`;
      const payload = mode === "login"
        ? { email: form.email, password: form.password }
        : { email: form.email, password: form.password, name: form.name, age_confirmed: ageConfirmed };
      const { data } = await axios.post(url, payload, { withCredentials: true });
      setUser(data);
      navigate(mode === "register" ? "/onboarding" : "/app");
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setBusy(false);
    }
  };

  const googleLogin = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/app";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-slate-50 noise-bg flex flex-col">
      <div className="flex-1 flex items-center justify-center px-4 py-16 relative z-10">
        <div className="w-full max-w-md">
          <div className="flex items-center gap-2 justify-center mb-8">
            <Lightning size={28} weight="fill" className="text-[#00E676]" />
            <span className="font-display text-3xl font-black uppercase tracking-tight">
              Parlay<span className="text-[#00E676]">Labs</span>
            </span>
          </div>

          <div className="bg-[#141419] border border-white/10 rounded-xl p-6 sm:p-8">
            <div className="grid grid-cols-2 gap-2 mb-6 bg-black/40 rounded-lg p-1">
              {["login", "register"].map((m) => (
                <button key={m} onClick={() => { setMode(m); setError(""); }} data-testid={`auth-tab-${m}`}
                  className={`rounded-md py-2 text-sm font-bold uppercase tracking-wide transition-colors ${
                    mode === m ? "bg-[#2563EB] text-white" : "text-slate-400 hover:text-white"}`}>
                  {m === "login" ? "Sign in" : "Sign up"}
                </button>
              ))}
            </div>

            <form onSubmit={submit} className="space-y-4">
              {mode === "register" && (
                <input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
                  placeholder="Name" data-testid="auth-name-input"
                  className="w-full rounded-lg bg-black/40 border border-white/10 px-4 py-3 text-sm focus:outline-none focus:border-[#2563EB] transition-colors" />
              )}
              <input required type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })}
                placeholder="Email" data-testid="auth-email-input"
                className="w-full rounded-lg bg-black/40 border border-white/10 px-4 py-3 text-sm focus:outline-none focus:border-[#2563EB] transition-colors" />
              <input required type="password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })}
                placeholder="Password" data-testid="auth-password-input"
                className="w-full rounded-lg bg-black/40 border border-white/10 px-4 py-3 text-sm focus:outline-none focus:border-[#2563EB] transition-colors" />

              {mode === "register" && (
                <label className="flex items-start gap-3 text-xs text-slate-400 leading-relaxed cursor-pointer">
                  <input type="checkbox" checked={ageConfirmed} onChange={(e) => setAgeConfirmed(e.target.checked)}
                    data-testid="age-gate-checkbox" className="mt-0.5 h-4 w-4 accent-[#00E676]" />
                  <span>I confirm I am <span className="text-white font-bold">21 or older</span> and understand ParlayLabs
                  is a sports analytics tool for entertainment only — not a sportsbook.</span>
                </label>
              )}

              {error && <p className="text-sm text-[#FF3366] font-medium" data-testid="auth-error">{error}</p>}

              {mode === "login" && (
                <Link to="/forgot-password" data-testid="forgot-password-link"
                  className="block text-right text-xs font-semibold text-slate-500 hover:text-[#2563EB] transition-colors">
                  Forgot password?
                </Link>
              )}

              <button type="submit" disabled={busy} data-testid="auth-submit-btn"
                className="w-full rounded-full bg-[#00E676] text-black font-bold py-3 text-sm hover:scale-[1.02] active:scale-95 transition-transform disabled:opacity-50">
                {busy ? "Working..." : mode === "login" ? "Sign in" : "Create free account"}
              </button>
            </form>

            <div className="flex items-center gap-3 my-5">
              <div className="h-px flex-1 bg-white/10" />
              <span className="text-xs text-slate-500 font-medium">or</span>
              <div className="h-px flex-1 bg-white/10" />
            </div>

            <button onClick={googleLogin} data-testid="google-login-btn"
              className="w-full rounded-full border border-white/15 bg-white/5 hover:bg-white/10 transition-colors py-3 text-sm font-bold flex items-center justify-center gap-2">
              <GoogleLogo size={18} weight="bold" /> Continue with Google
            </button>
            <p className="text-[11px] text-slate-500 mt-3 text-center">
              By continuing with Google you confirm you are 21+.
            </p>
          </div>
        </div>
      </div>
      <Disclaimer />
    </div>
  );
}
